// PackagesManagement: Admin CRUD for packages, link to events

import React, { useEffect, useState } from 'react';
import SuperAdminLayout from '../../layouts/SuperAdminLayout';
import { Box, Typography, Button, Table, TableHead, TableRow, TableCell, TableBody, Chip } from '@mui/material';
import api from '../../services/api';
import NotificationSnackbar from '../../components/NotificationSnackbar';
import PackageModal from '../../components/PackageModal';
import HotelModal from '../../components/HotelModal';


function PackagesManagement() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [modalOpen, setModalOpen] = useState(false);
  const [editPackage, setEditPackage] = useState(null);
  const [hotelModalOpen, setHotelModalOpen] = useState(false);
  const [hotels, setHotels] = useState([]);
  const [editHotel, setEditHotel] = useState(null);


  const fetchPackages = () => {
    setLoading(true);
    api.get('/api/packages')
      .then(res => setPackages(res.data.packages || []))
      .catch(err => setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to fetch packages', severity: 'error' }))
      .finally(() => setLoading(false));
  };
  const fetchHotels = () => {
    api.get('/api/hotels')
      .then(res => setHotels(res.data.hotels || []));
  };
  useEffect(() => { fetchPackages(); fetchHotels(); }, []);

  const handleDelete = async (id) => {
    setLoading(true);
    setSnackbar({ open: false, message: '', severity: 'info' });
    try {
      await api.delete(`/api/packages/${id}`);
      setSnackbar({ open: true, message: 'Package deleted successfully', severity: 'success' });
      fetchPackages();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Delete failed', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => setSnackbar(s => ({ ...s, open: false }));
  const handleCreate = () => { setEditPackage(null); setModalOpen(true); };
  const handleEdit = (pkg) => { setEditPackage(pkg); setModalOpen(true); };
  const handleModalSave = () => { setModalOpen(false); fetchPackages(); };
  const handleHotelModalSave = () => { setHotelModalOpen(false); fetchHotels(); };

  return (
    <SuperAdminLayout>
      <Box sx={{ mb: 4, px: { xs: 1, md: 0 } }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Packages Management
        </Typography>
        <Button variant="contained" color="primary" sx={{ mb: 2, borderRadius: 2, fontWeight: 700 }} onClick={handleCreate}>Create Package</Button>
        <Button variant="outlined" color="secondary" sx={{ mb: 2, ml: 2 }} onClick={() => { setEditHotel(null); setHotelModalOpen(true); }}>Manage Hotels</Button>
        {loading ? <Typography>Loading...</Typography> : (
          <Table sx={{ borderRadius: 2, boxShadow: 2, overflow: 'hidden' }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.paper' }}>
                <TableCell>Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Capacity</TableCell>
                <TableCell>Includes</TableCell>
                <TableCell>Hotels</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {packages.map((pkg) => (
                <TableRow key={pkg._id}>
                  <TableCell>{pkg.name}</TableCell>
                  <TableCell>{pkg.category}</TableCell>
                  <TableCell>{pkg.price}</TableCell>
                  <TableCell>{pkg.capacity}</TableCell>
                  <TableCell>{(pkg.includes || []).map((inc, i) => <Chip key={i} label={inc} size="small" sx={{ mr: 0.5 }} />)}</TableCell>
                  <TableCell>{(pkg.hotels || []).map(h => <Chip key={h._id} label={h.name} size="small" sx={{ mr: 0.5 }} />)}</TableCell>
                  <TableCell>
                    <Button size="small" variant="outlined" onClick={() => handleEdit(pkg)}>Edit</Button>
                    <Button size="small" color="error" variant="outlined" sx={{ ml: 1 }} onClick={() => handleDelete(pkg._id)}>Delete</Button>
                    <Button size="small" color="primary" variant="outlined" sx={{ ml: 1 }} component="a" href={`/packages/${pkg._id}`} target="_blank">View</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <PackageModal open={modalOpen} onClose={() => setModalOpen(false)} onSave={handleModalSave} editPackage={editPackage} />
        <HotelModal open={hotelModalOpen} onClose={() => setHotelModalOpen(false)} onSave={handleHotelModalSave} />
        {/* Show hotels below for immediate feedback */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Hotels / Accommodation</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Rooms</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {hotels.map(hotel => (
                <TableRow key={hotel._id}>
                  <TableCell>{hotel.name}</TableCell>
                  <TableCell>{hotel.address}</TableCell>
                  <TableCell>{(hotel.rooms || []).map((r, i) => <Chip key={i} label={`${r.type} - ${r.price}`} size="small" sx={{ mr: 0.5 }} />)}</TableCell>
                  <TableCell>
                    <Button size="small" variant="outlined" onClick={() => { setEditHotel(hotel); setHotelModalOpen(true); }}>Edit</Button>
                    <Button size="small" color="error" variant="outlined" sx={{ ml: 1 }} onClick={async () => {
                      if (!window.confirm('Delete this hotel?')) return;
                      try {
                        setSnackbar({ open: false, message: '', severity: 'info' });
                        await api.delete(`/api/hotels/${hotel._id}`);
                        setSnackbar({ open: true, message: 'Hotel deleted', severity: 'success' });
                        fetchHotels();
                      } catch (err) {
                        setSnackbar({ open: true, message: err.response?.data?.error || 'Delete failed', severity: 'error' });
                      }
                    }}>Delete</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        <NotificationSnackbar
          open={snackbar.open}
          onClose={handleSnackbarClose}
          message={snackbar.message}
          severity={snackbar.severity}
        />
      </Box>
    </SuperAdminLayout>
  );
}

export default PackagesManagement;
